import React, { useContext, useState } from 'react';
import { SyncLoader } from 'react-spinners';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Dropdown,
  DropdownItem,
  DropdownList,
  MenuToggle,
  MenuToggleElement,
  Switch,
} from '@patternfly/react-core';
import { TrashIcon } from '@patternfly/react-icons';
import { storesContext } from 'stores';
import { useFeature } from '@ole-ui/ole-ui-components';
import classNames from 'classnames';
import stopIcon from 'img/stop.svg';
import LabEnvironmentStore, { labEnvironmentContext } from '../store';
import LabInfo from '../LabInfo';
import LabErrorModal from '../LabErrorModal';

type LabButtonStateConfig = {
  label: string;
  variant: 'danger' | 'secondary' | 'primary';
  when: string[];
};

type LabControlsProps = {
  labStore: LabEnvironmentStore;
};

type ModalConfirmation = {
  confirmText: string;
  dismissText: string;
  title: string;
  modalType?: 'delete' | 'stop';
};

type ResolvedState = LabButtonStateConfig & {
  status: 'active' | 'working';
};

const LabStopIcon = () => (
  <img
    className="lab-card__stop-icon"
    src={stopIcon}
    alt=""
    aria-hidden="true"
  />
);

const getCommandIcon = (command: string) => {
  if (command === 'app_stop') {
    return <LabStopIcon />;
  }
  if (command === 'app_delete') {
    return <TrashIcon />;
  }
  return undefined;
};

const LabControls: React.FC<LabControlsProps> = ({ labStore }) => {
  const [isTemplateDropdownOpen, setIsTemplateDropdownOpen] = useState(false);
  const { t } = useTranslation();
  const { userStore } = useContext(storesContext);

  const canCreateOnStates = ['ready', 'deleted'];

  const isVtEnableDisableInternetAccessEnabled = useFeature({
    defaultFlagValue: false,
    flagName: 'vt_enable_disable_internet_access',
    section: 'lab',
  });

  const disconnectedInternetAccessSwitch = () => (
    <Switch
      className="disconnected-switch"
      hasCheckIcon
      isChecked={labStore.internetAccessDisconnected}
      isDisabled={labStore.internetAccessSwitchDisable}
      label={t('Internet Access Disconnected')}
      onChange={(_event, checked) => {
        if (!labStore.internetAccessCommandRequested) {
          labStore.setInternetAccessDisconnected(checked);
          labStore.doCommand('internet_access', { access: !checked });
        }
      }}
    />
  );

  const renderButtons = () => {
    const LabButton = (
      command: string,
      activeState: LabButtonStateConfig,
      workingState: LabButtonStateConfig,
      title: string,
      modalConfirmation: ModalConfirmation | undefined,
      analyticsId: string,
      buttonClassName?: string,
    ) => {
      let state: ResolvedState | null = null;
      if (labStore) {
        const { when: activeWhen } = activeState;
        const { when: workingWhen } = workingState;
        if (activeWhen.includes(labStore.labState)) {
          const { label, variant, when } = activeState;
          state = { label, status: 'active', variant, when };
        } else if (workingWhen.includes(labStore.labState)) {
          const { label, variant, when } = workingState;
          state = { label, status: 'working', variant, when };
        }
      }

      let isFeatureEnabled = useFeature({
        defaultFlagValue: true,
        flagName: command,
        section: 'lab',
      });

      if (state !== null) {
        const isLabControlDisabled =
          !labStore.hasLabHours ||
          state.status !== 'active' ||
          (canCreateOnStates.includes(labStore.labState) &&
            !labStore.canLabStart);

        isFeatureEnabled =
          command === 'launch' && !isLabControlDisabled
            ? true
            : isFeatureEnabled;
      }

      return state !== null && isFeatureEnabled ? (
        <Button
          className={classNames('has-spinner', buttonClassName, {
            'spinner-active': state.status === 'working',
          })}
          data-analytics-id={analyticsId}
          isDisabled={
            !labStore.hasLabHours ||
            state.status !== 'active' ||
            (canCreateOnStates.includes(labStore.labState) &&
              !labStore.canLabStart)
          }
          key={command}
          onClick={() =>
            modalConfirmation
              ? labStore.createNewModal(
                  {
                    ...modalConfirmation,
                    confirmFunc: () => {
                      labStore.doCommand(command);
                      labStore.hideModal();
                    },
                  },
                  true,
                )
              : labStore.doCommand(command)
          }
          title={title}
          variant={state.variant}
          icon={getCommandIcon(command)}
        >
          <div className="spinner-wrapper">
            <SyncLoader color="#6a6e73" size={8} />
          </div>
          {state.label}
        </Button>
      ) : null;
    };

    const LabTemplateSelector = () =>
      labStore.availableTemplates.length && labStore.labState === 'ready' ? (
        <Dropdown
          isOpen={isTemplateDropdownOpen}
          onOpenChange={(open: boolean) => setIsTemplateDropdownOpen(open)}
          onSelect={() => setIsTemplateDropdownOpen(false)}
          toggle={(toggleRef: React.Ref<MenuToggleElement>) => (
            <MenuToggle
              isExpanded={isTemplateDropdownOpen}
              onClick={() => setIsTemplateDropdownOpen((prev) => !prev)}
              ref={toggleRef}
              variant="secondary"
            >
              {labStore.currentTemplate.title}
            </MenuToggle>
          )}
        >
          <DropdownList>
            {labStore.availableTemplates.map(
              (
                template: {
                  child_offering_slug?: string;
                  id: string;
                  title: string;
                },
                i: number,
              ) => (
                <DropdownItem
                  key={template.child_offering_slug || template.id}
                  onClick={() => {
                    labStore.setCurrentTemplate(i);
                  }}
                >
                  {template.title}
                </DropdownItem>
              ),
            )}
          </DropdownList>
        </Dropdown>
      ) : null;

    return [
      LabTemplateSelector(),
      LabButton(
        'launch',
        {
          label: t('Create'),
          variant: 'primary',
          when: ['ready', 'deleted'],
        },
        {
          label: t('Creating'),
          variant: 'secondary',
          when: ['requested', 'creating', 'installing', 'transitioning'],
        },
        t('Create the lab'),
        undefined,
        'create-vt-dash-lp',
      ),
      LabButton(
        'app_start',
        {
          label: t('Start'),
          variant: 'secondary',
          when: ['stopped'],
        },
        {
          label: t('Starting'),
          variant: 'secondary',
          when: ['starting'],
        },
        t('Start the lab'),
        undefined,
        'start-vt-dash-lp',
      ),
      LabButton(
        'app_stop',
        {
          label: t('Stop'),
          variant: 'secondary',
          when: ['running'],
        },
        {
          label: t('Stopping'),
          variant: 'secondary',
          when: ['stopping'],
        },
        t('Stop the lab'),
        {
          confirmText: t('Stop'),
          dismissText: t('Cancel'),
          title: t('Stop lab environment'),
          modalType: 'stop',
        },
        'stop-vt-dash-lp',
        'lab-card__stop-btn',
      ),
      LabButton(
        'app_delete',
        {
          label: t('Delete'),
          variant: 'danger',
          when: ['running', 'stopped', 'failed', 'starting', 'stopping'],
        },
        {
          label: t('Deleting'),
          variant: 'secondary',
          when: ['deleting'],
        },
        t('Delete the lab'),
        {
          confirmText: t('Delete'),
          dismissText: t('Cancel'),
          title: t('Delete lab environment'),
          modalType: 'delete',
        },
        'delete-vt-dash-lp',
        'lab-card__delete-btn',
      ),
    ];
  };

  return (
    <div className="lab-card__footer-inner">
      <div className="lab-card__footer-actions">{renderButtons()}</div>
      {labStore.labState !== 'ready' ? (
        <labEnvironmentContext.Provider value={labStore}>
          {labStore.labState ? (
            <div className="lab-card__footer-meta">
              <LabInfo />
              {labStore.labState === 'failed' &&
                (userStore.isAdminOrOperations || userStore.isInstructor) && (
                  <LabErrorModal labStore={labStore} />
                )}
            </div>
          ) : null}
        </labEnvironmentContext.Provider>
      ) : null}
      {labStore.labState !== 'ready' &&
        isVtEnableDisableInternetAccessEnabled &&
        userStore.isAdminOrOperations && (
          <div className="lab-card__internet-access-switch">
            {disconnectedInternetAccessSwitch()}
          </div>
        )}
    </div>
  );
};

export default observer(LabControls);
